// src/components/DocumentList.js

import React, { useState } from 'react';
import { 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction, 
  IconButton, 
  Typography, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  CircularProgress
} from '@mui/material';
import { Delete, Download, Visibility } from '@mui/icons-material';
import { deleteDocumentViaPresignedUrl, downloadDocument, getDocumentViewUrl } from '../services/api';

function DocumentList({ userGuid, documents, accessToken, onDocumentDeleted, showDeleteButton = true }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('');

  const handleDownloadDocument = async (document) => {
    if (!document || !document.name) {
      console.error('Invalid document selected for download');
      return;
    }
    
    try {
      await downloadDocument(userGuid, document.name, accessToken);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleViewDocument = async (document) => {
    if (!document || !document.name) {
      console.error('Invalid document selected for viewing');
      return;
    }
    
    try {
      const viewUrl = await getDocumentViewUrl(userGuid, document.name, accessToken);
      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
    }
  };

  const handleDeleteClick = (document) => {
    console.log('Document to delete (fromdocumentlist):', document);
    setSelectedDocument(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedDocument(null);
    setDeleteStatus('');
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDocument || !selectedDocument.name) {
      setDeleteStatus('Error: Invalid document selected');
      return;
    }
    
    setIsDeleting(true);
    setDeleteStatus('Deleting document...');
    
    try {
      const response = await deleteDocumentViaPresignedUrl(userGuid, selectedDocument.name, accessToken);
      
      if (response.success) {
        setDeleteStatus('Document deleted successfully!');
        // If a callback was provided, call it to refresh the document list
        if (onDocumentDeleted) {
          onDocumentDeleted();
        }
        // Close the dialog after a short delay
        setTimeout(() => {
          setDeleteDialogOpen(false);
          setSelectedDocument(null);
          setDeleteStatus('');
        }, 1500);
      } else {
        setDeleteStatus(`Failed to delete document: ${response.message}`);
      }
    } catch (error) {
      console.error('Error in delete handler:', error);
      setDeleteStatus('An error occurred while deleting the document.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!documents || documents.length === 0) {
    return (
      <Typography variant="body1" sx={{ mt: 2, fontStyle: 'italic' }}>
        No documents found.
      </Typography>
    );
  }

  return (
    <>
      <List>
        {documents.map((doc, index) => (
         <ListItem key={index} divider>
         <ListItemText 
           primary={doc.name} 
           secondary={doc.lastModified ? new Date(doc.lastModified).toLocaleString() : ''}
         />
         <ListItemSecondaryAction>
           {doc.name && (
             <>
               <IconButton 
                 edge="end" 
                 aria-label="view" 
                 onClick={() => handleViewDocument(doc)}
                 sx={{ mr: 1 }}
               >
                 <Visibility />
               </IconButton>
               <IconButton 
                 edge="end" 
                 aria-label="download" 
                 onClick={() => handleDownloadDocument(doc)}
                 sx={{ mr: 1 }}
               >
                 <Download />
               </IconButton>
             </>
           )}
           {showDeleteButton && (
             <IconButton 
               edge="end" 
               aria-label="delete" 
               onClick={() => handleDeleteClick(doc)}
               color="error"
           >
             <Delete />
              </IconButton>
            )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      {/* Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Document Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this document? This action cannot be undone.
            {selectedDocument && selectedDocument.name && (
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
                {selectedDocument.name}
              </Typography>
            )}
          </DialogContentText>
          {isDeleting && <CircularProgress size={24} sx={{ mt: 2, display: 'block', mx: 'auto' }} />}
          {deleteStatus && (
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 2, 
                color: deleteStatus.includes('successfully') ? 'green' : 'red',
                textAlign: 'center'
              }}
            >
              {deleteStatus}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={isDeleting}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DocumentList;
